/**
 * Composable Schema.org JSON-LD builders.
 *
 * Why this file exists: schema.org payloads inlined in page components are
 * 100+ lines of nested objects. This module produces typed builder functions
 * that compose via `graph(...)` and share a single source of truth for site
 * constants (BASE_URL, organization details).
 *
 * Output shape: each builder returns a Record<string, unknown> matching the
 * `jsonLd` prop on `<SEOHead>`. Compose multiple nodes via `graph()`.
 *
 * Validation: paste output into https://validator.schema.org/ or
 * https://search.google.com/test/rich-results to verify.
 */

const BASE_URL = 'https://ratygo.pl'
const SITE_NAME = 'RatyGo'

/** Stable @id for the Organization node — referenced by other nodes via `{ '@id': ORG_ID }`. */
const ORG_ID = `${BASE_URL}#organization`
/** Stable @id for the WebApplication node. */
const WEBAPP_ID = `${BASE_URL}#webapp`

export interface BreadcrumbItem {
  name: string
  /** Absolute URL or path starting with `/`. Paths are joined with BASE_URL. */
  url: string
}

export interface FaqItem {
  question: string
  answer: string
}

/**
 * Schema-friendly subset of an article's metadata. ArticleMeta in
 * `src/pages/blog/articles/index.ts` extends this and adds the React component.
 *
 * Designed so jsonLd.ts has zero runtime imports from page modules.
 */
export interface ArticleNode {
  slug: string
  title: string
  description: string
  keywords?: string[]
  publishedAt: string
  /** ISO date of last meaningful edit. Falls back to publishedAt when unset. */
  dateModified?: string
  /** OG image. Relative path (`/og-blog-foo.png`) or absolute URL. Defaults to `/og-image.png`. */
  image?: string
}

/** Resolve an article image to an absolute URL, falling back to the site default. */
function resolveImageUrl(image: string | undefined): string {
  if (!image) return `${BASE_URL}/og-image.png`
  if (image.startsWith('http')) return image
  return `${BASE_URL}${image.startsWith('/') ? image : `/${image}`}`
}

/**
 * Organization node. Used as `publisher` reference (via @id) and as a standalone
 * graph node so search engines can resolve the publisher's identity once and
 * reuse it across pages.
 *
 * Currently anonymous (no Person author/founder) — see Phase 2 audit decision.
 * Add `sameAs: [...]` later when social profiles exist.
 */
export function organization(): Record<string, unknown> {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    url: BASE_URL,
    logo: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/og-image.png`,
      width: 1200,
      height: 630,
    },
    description:
      'Polski kalkulator kredytu hipotecznego oraz materiały edukacyjne dla osób kupujących nieruchomość w Polsce.',
    areaServed: { '@type': 'Country', name: 'Polska' },
    inLanguage: 'pl',
  }
}

/**
 * WebApplication node — the calculator tool itself. Belongs on the homepage.
 *
 * `applicationCategory: FinanceApplication` is the Schema.org-recognized value
 * that signals to AI/search this is a finance tool. `featureList` is the most
 * AI-quotable field — keep entries concrete and parseable.
 */
export function webApplication(): Record<string, unknown> {
  return {
    '@type': 'WebApplication',
    '@id': WEBAPP_ID,
    name: SITE_NAME,
    url: BASE_URL,
    description:
      'Interaktywny kalkulator kredytu hipotecznego dla polskiego rynku. Porównaj raty równe i malejące, symuluj nadpłaty, sprawdź zdolność kredytową.',
    applicationCategory: 'FinanceApplication',
    operatingSystem: 'Any',
    browserRequirements: 'Wymaga JavaScript. Działa we wszystkich nowoczesnych przeglądarkach.',
    inLanguage: 'pl',
    isAccessibleForFree: true,
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'PLN' },
    publisher: { '@id': ORG_ID },
    screenshot: {
      '@type': 'ImageObject',
      url: `${BASE_URL}/og-image.png`,
      width: 1200,
      height: 630,
    },
    audience: {
      '@type': 'Audience',
      audienceType: 'Osoby kupujące nieruchomość w Polsce',
      geographicArea: { '@type': 'Country', name: 'Polska' },
    },
    featureList: [
      'Porównanie rat równych (annuitetowych) i malejących',
      'Kalkulacja zdolności kredytowej na podstawie miesięcznego dochodu',
      'Symulacja nadpłat kredytu — oszczędności na odsetkach i skrócenie okresu spłaty',
      'Uwzględnienie WIBOR i marży banku',
      'Dodatkowe koszty kredytu: prowizja banku, ubezpieczenie, podatek PCC, opłata notarialna',
      'Porównanie zakupu na kredyt z wynajmem mieszkania',
      'Zapisywanie i porównanie do 3 scenariuszy obok siebie',
      'Udostępnianie wyniku przez link z zachowaniem wszystkich parametrów',
    ],
    keywords:
      'kalkulator kredytu hipotecznego, kredyt hipoteczny, raty równe, raty malejące, WIBOR, zdolność kredytowa, nadpłata kredytu',
  }
}

/**
 * BreadcrumbList. Pass crumbs in order from root → current page.
 * URLs may be absolute or paths starting with `/`.
 *
 * Skip breadcrumbs with a single item — Google ignores them and some validators warn.
 */
export function breadcrumbList(items: BreadcrumbItem[]): Record<string, unknown> {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`,
    })),
  }
}

/**
 * FAQPage. The single highest-value schema for AI citation — Question/Answer
 * pairs get extracted verbatim into AI search results and Google's "People also ask".
 *
 * Quality bar: each Q is a real user question (not marketing); each A leads
 * with the answer in the first sentence and stays under ~3 sentences.
 */
export function faqPage(faqs: FaqItem[]): Record<string, unknown> {
  return {
    '@type': 'FAQPage',
    mainEntity: faqs.map(f => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: f.answer,
      },
    })),
  }
}

/**
 * Article node for blog post pages. Falls back gracefully when optional fields
 * (dateModified, image, keywords) are unset.
 *
 * `author` and `publisher` reference the Organization via @id — emit
 * organization() in the same @graph for resolution.
 */
export function article(meta: ArticleNode): Record<string, unknown> {
  const url = `${BASE_URL}/blog/${meta.slug}`
  const node: Record<string, unknown> = {
    '@type': 'Article',
    '@id': `${url}#article`,
    headline: meta.title,
    description: meta.description,
    datePublished: meta.publishedAt,
    dateModified: meta.dateModified ?? meta.publishedAt,
    author: { '@id': ORG_ID },
    publisher: { '@id': ORG_ID },
    mainEntityOfPage: url,
    url,
    image: {
      '@type': 'ImageObject',
      url: resolveImageUrl(meta.image),
      width: 1200,
      height: 630,
    },
    inLanguage: 'pl',
  }
  if (meta.keywords && meta.keywords.length > 0) {
    node.keywords = meta.keywords.join(', ')
  }
  return node
}

/**
 * Blog node — the /blog index page. Lists all articles as embedded BlogPosting
 * entries so AI tools see the full site structure in one fetch.
 *
 * Pass articles pre-sorted (newest first works best for AI summaries).
 */
export function blog(articles: ArticleNode[]): Record<string, unknown> {
  return {
    '@type': 'Blog',
    '@id': `${BASE_URL}/blog#blog`,
    name: 'Blog RatyGo',
    description:
      'Artykuły o kredytach hipotecznych w Polsce — raty równe vs malejące, nadpłaty, WIBOR, zdolność kredytowa, RRSO i więcej.',
    url: `${BASE_URL}/blog`,
    inLanguage: 'pl',
    publisher: { '@id': ORG_ID },
    blogPost: articles.map(a => {
      const url = `${BASE_URL}/blog/${a.slug}`
      const post: Record<string, unknown> = {
        '@type': 'BlogPosting',
        headline: a.title,
        description: a.description,
        datePublished: a.publishedAt,
        dateModified: a.dateModified ?? a.publishedAt,
        url,
        mainEntityOfPage: url,
        image: resolveImageUrl(a.image),
        author: { '@id': ORG_ID },
        publisher: { '@id': ORG_ID },
        inLanguage: 'pl',
      }
      if (a.keywords && a.keywords.length > 0) {
        post.keywords = a.keywords.join(', ')
      }
      return post
    }),
  }
}

/**
 * Wrap multiple schema nodes in a single @graph payload. Use when a page has
 * more than one top-level entity (e.g. WebApplication + Organization + FAQPage).
 *
 * Nodes can cross-reference each other via stable @id values (see ORG_ID).
 */
export function graph(...nodes: Record<string, unknown>[]): Record<string, unknown> {
  return {
    '@context': 'https://schema.org',
    '@graph': nodes,
  }
}
